/**
 * Neutral geo kernel (S021; REQ-GAP-007, C-02, REQ-BM-018).
 *
 * The vocabulary every geo port shares. It is deliberately provider-agnostic and country-agnostic:
 *
 * - Coordinates are WGS84 `{ latitude, longitude }` objects — never a `[lng, lat]` tuple whose order a
 *   caller can invert (a real donor bug: some providers want `lng,lat`, GeoJSON `[lng,lat]`, the domain `{lat,lng}`).
 * - Distance is **meters**, duration is **seconds**, bearing is **degrees**. No kilometers, no "min".
 * - A provider's native place id never gains a vendor-typed field: it is wrapped in an opaque {@link PlaceRef}.
 * - Language/region are request inputs (see `GeoRequestOptions`), never a hardcoded locale or bounding box.
 * - Match quality is a Vello-owned {@link Precision} scale that adapters map *into* — the domain never
 *   inherits one vendor's `location_type` vocabulary.
 *
 * `LatLng` is structurally identical to `@vello/config`'s market-anchor `GeoPoint`, so a market center flows
 * into a geo request with no conversion; geo stays a `@vello/core`-only leaf and does not depend on config.
 */
/** Mean Earth radius (metres) for spherical (haversine) distance — a geodesic constant, not a locale. */
export const EARTH_RADIUS_METERS = 6_371_008.8;
export function isLatLng(v) {
    return (Number.isFinite(v.latitude) &&
        v.latitude >= -90 &&
        v.latitude <= 90 &&
        Number.isFinite(v.longitude) &&
        v.longitude >= -180 &&
        v.longitude <= 180);
}
/** Smart constructor: a `LatLng` cannot exist off the sphere. Throws `RangeError` on an invalid coordinate. */
export function latLng(latitude, longitude) {
    const p = { latitude, longitude };
    if (!isLatLng(p)) {
        throw new RangeError(`LatLng out of range: latitude ${latitude} must be in [-90, 90], longitude ${longitude} in [-180, 180]`);
    }
    return p;
}
/** Whether a point lies within a bounds (inclusive). Longitude wrap-around across the antimeridian is out of scope. */
export function boundsContain(b, p) {
    return (p.latitude >= b.southWest.latitude &&
        p.latitude <= b.northEast.latitude &&
        p.longitude >= b.southWest.longitude &&
        p.longitude <= b.northEast.longitude);
}
/** Metres per degree of latitude on the spherical Earth {@link haversineMeters} uses (mean radius 6,371,008.8 m). */
const METERS_PER_DEGREE_LATITUDE = (Math.PI * 6_371_008.8) / 180;
/**
 * D822: the rectangle that holds every point within `radiusMeters` of `center` (a search box around where the person is). The latitude is clamped to the
 * poles and the longitude to +-180 (a box across the antimeridian is out of scope, as in {@link boundsContain}).
 */
export function boundsAround(center, radiusMeters) {
    const r = Math.max(0, radiusMeters);
    const dLat = r / METERS_PER_DEGREE_LATITUDE;
    const cos = Math.cos((Math.min(89.9, Math.abs(center.latitude) + dLat) * Math.PI) / 180);
    const dLng = Math.min(180, r / (METERS_PER_DEGREE_LATITUDE * Math.max(cos, 1e-6)));
    const clampLat = (v) => Math.max(-90, Math.min(90, v));
    const clampLng = (v) => Math.max(-180, Math.min(180, v));
    return {
        southWest: { latitude: clampLat(center.latitude - dLat), longitude: clampLng(center.longitude - dLng) },
        northEast: { latitude: clampLat(center.latitude + dLat), longitude: clampLng(center.longitude + dLng) },
    };
}
/** Great-circle distance in metres (spherical Earth). Pure and universal; provider distances are preferred when available. */
export function haversineMeters(a, b) {
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.latitude - a.latitude);
    const dLng = toRad(b.longitude - a.longitude);
    const lat1 = toRad(a.latitude);
    const lat2 = toRad(b.latitude);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}
/** Rank of a {@link Precision}; higher is a better match. Lets a cascade keep a best-so-far result. */
export const PRECISION_RANK = {
    precise: 4,
    interpolated: 3,
    centroid: 2,
    approximate: 1,
};
//# sourceMappingURL=location.js.map